import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ResourceHeader from './resource/ResourceHeader';
import BasicInformation from './resource/BasicInformation';
import PermissionRoleSection from './resource/PermissionRoleSection';
import CompensationSection from './resource/CompensationSection';
import AvailabilitySection from './resource/AvailabilitySection';
import { Resource, updateResource, activateResource, deleteResource } from '../lib/resources';
import { useBusiness } from '../contexts/BusinessContext';
import { X, AlertCircle, CheckCircle, Activity, UserCheck, UserX, Key } from 'lucide-react';
import ChangePasswordModal from './ChangePasswordModal';

interface ResourceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resource: Resource | null;
  onUpdate: () => void;
}

export default function ResourceDetailsModal({ isOpen, onClose, resource, onUpdate }: ResourceDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState<{ info?: boolean; pricing?: boolean }>({});
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Resource>>({});
  const [localResource, setLocalResource] = useState<Resource | null>(null);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [activateLoading, setActivateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // Function to completely reset all state
  const resetAllState = () => {
    console.log("Completely resetting resource modal state");
    setFormData({});
    setLocalResource(null);
    setShowDeleteConfirm(false);
    setEditMode(null);
    setError(null);
    setLoading({});
    setDeactivateLoading(false);
    setActivateLoading(false);
    setDeleteLoading(false);
    setShowChangePasswordModal(false);
  };

  useEffect(() => {
    if (isOpen && resource) {
      console.log(`Opening resource modal for: ${resource.id} - ${resource.first_name} ${resource.last_name}`);
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      
      // Initialize form data with both commission fields synchronized
      const initialFormData = {
        ...resource,
        // Ensure both commission fields are set if either exists
        commission_percentage: resource.commission_percentage || resource.commission_rate || 0,
        commission_rate: resource.commission_rate || resource.commission_percentage || 0
      };
      
      setFormData(initialFormData);
      setLocalResource(resource);
      setShowDeleteConfirm(false);
    } else {
      console.log("Closing resource modal, cleaning up state");
      document.body.style.overflow = 'unset';
      
      // When modal is being closed, completely reset state
      if (!isOpen) {
        resetAllState();
      }
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, resource]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSave = async (section: string) => {
    if (!resource) return;

    try {
      setLoading({ info: true, pricing: true });
      setError(null);

      // Filter out fields that we've hidden and shouldn't be updated
      const { role, permission_role, ...cleanFormData } = formData;
      
      // Ensure numeric fields are properly converted
      if (cleanFormData.hourly_rate !== undefined) {
        cleanFormData.hourly_rate = Number(cleanFormData.hourly_rate) || 0;
      }
      if (cleanFormData.flat_rate_amount !== undefined) {
        cleanFormData.flat_rate_amount = Number(cleanFormData.flat_rate_amount) || 0;
      }
      
      // Handle commission compensation - ensure both commission_rate and commission_percentage are set and are numbers
      if (cleanFormData.compensation_type === 'commission') {
        const commissionValue = Number(cleanFormData.commission_percentage) || 0;
        cleanFormData.commission_percentage = commissionValue;
        cleanFormData.commission_rate = commissionValue;
      }
      
      // Validate commission percentage
      if (cleanFormData.compensation_type === 'commission' && cleanFormData.commission_percentage !== undefined && 
          (cleanFormData.commission_percentage < 0 || cleanFormData.commission_percentage > 100)) {
        throw new Error('Commission percentage must be between 0 and 100');
      }
      
      // Ensure required fields are properly set to avoid constraint violations
      if (cleanFormData.is_mobile_available === undefined || cleanFormData.is_mobile_available === null) {
        cleanFormData.is_mobile_available = false;
      }
      
      // Ensure compensation_type is valid
      if (!cleanFormData.compensation_type || !['hourly', 'flat_rate', 'commission'].includes(cleanFormData.compensation_type)) {
        cleanFormData.compensation_type = 'hourly';
      }
      
      console.log('=== RESOURCE SAVE DEBUG ===');
      console.log('Original resource:', JSON.stringify(resource, null, 2));
      console.log('Form data before clean:', JSON.stringify(formData, null, 2));
      console.log('Clean data being sent:', JSON.stringify(cleanFormData, null, 2));
      console.log('Compensation type check:', cleanFormData.compensation_type);
      console.log('Mobile available check:', cleanFormData.is_mobile_available);
      console.log('=== END DEBUG ===');

      const updatedResource = await updateResource(resource.id, cleanFormData);
      
      setLocalResource(updatedResource);
      setEditMode(null);
      
      onUpdate();
    } catch (err) {
      console.error('=== ERROR DETAILS ===');
      console.error('Full error object:', err);
      console.error('Error message:', err instanceof Error ? err.message : 'Unknown error');
      console.error('Error stack:', err instanceof Error ? err.stack : 'No stack trace');
      console.error('FormData that failed:', JSON.stringify(formData, null, 2));
      
      // Try to extract more details from the error
      if (err && typeof err === 'object') {
        console.error('Error details:', JSON.stringify(err, null, 2));
        if ('details' in err) {
          console.error('Error details property:', err.details);
        }
        if ('hint' in err) {
          console.error('Error hint:', err.hint);
        }
        if ('code' in err) {
          console.error('Error code:', err.code);
        }
      }
      console.error('=== END ERROR DETAILS ===');
      
      // Provide more specific error messages
      let errorMessage = 'Failed to update team member';
      if (err instanceof Error) {
        console.log('Full error message:', err.message);
        
        if (err.message.includes('permission')) {
          errorMessage = 'Permission denied: You do not have access to update this team member';
        } else if (err.message.includes('check constraint')) {
          if (err.message.includes('compensation_type')) {
            errorMessage = 'Invalid compensation type. Must be hourly, flat_rate, or commission.';
          } else if (err.message.includes('is_mobile_available')) {
            errorMessage = 'Mobile availability setting is required.';
          } else {
            errorMessage = `Database validation error: ${err.message}`;
          }
        } else if (err.message.includes('constraint') || err.message.includes('violates')) {
          errorMessage = `Database constraint violation: ${err.message}`;
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = 'Network error: Please check your connection and try again';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading({ info: false, pricing: false });
    }
  };

  const handleDeactivate = async () => {
    if (!resource) return;

    try {
      setDeactivateLoading(true);
      setError(null);

      const updatedResource = await updateResource(resource.id, {
        status: 'inactive'
      });
      
      setLocalResource(updatedResource);
      onUpdate();
    } catch (err) {
      console.error('Error deactivating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate team member');
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!resource) return;

    try {
      setActivateLoading(true);
      setError(null);

      // Don't use activateResource here as it only changes the status
      // Instead use updateResource to update the status
      const updatedResource = await updateResource(resource.id, {
        status: 'active'
      });
      
      setLocalResource(updatedResource);
      onUpdate();
    } catch (err) {
      console.error('Error activating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate team member');
    } finally {
      setActivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!resource) return;
    
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }
    
    try {
      setDeleteLoading(true);
      setError(null);
      
      await deleteResource(resource.id);
      
      onUpdate();
      handleClose();
    } catch (err) {
      console.error('Error deleting resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete team member');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Add a custom close handler that ensures cleanup
  const handleClose = () => {
    console.log("Resource modal close handler called");
    // Reset state before closing
    resetAllState();
    // Call the provided onClose function
    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!localResource) return null;

  const modalContent = (
    <div 
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0'
      }`}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`fixed inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          touchAction: 'manipulation',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
          paddingLeft: 'env(safe-area-inset-left)',
          paddingRight: 'env(safe-area-inset-right)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div className="h-screen flex flex-col">
          {/* Header with Mobile-Optimized Design */}
          <div className="relative bg-gray-800 border-b border-gray-700 shadow-lg">
            {/* Mobile Header Layout */}
            <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-4">
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <button 
                  onClick={handleClose} 
                  className="flex items-center justify-center w-8 h-8 sm:w-11 sm:h-11 rounded-lg sm:rounded-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation shadow-sm"
                  style={{ minWidth: '32px', minHeight: '32px' }}
                >
                  <X className="h-4 w-4 sm:h-6 sm:w-6" />
                </button>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-white truncate">
                    {localResource.first_name} {localResource.last_name}
                  </h2>
                  <div className="flex items-center mt-0.5">
                    <div className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full ${getStatusColor(localResource.status)} mr-1.5 sm:mr-2 shadow-sm`} />
                    <span className="text-xs sm:text-sm text-gray-300 truncate">
                      {localResource.status === 'active' ? 'Active team member' : 'Inactive team member'}
                    </span>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Content with mobile-optimized styling */}
          <div className="flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="max-w-5xl mx-auto py-1 px-2 sm:py-6 sm:px-6">
              {error && (
                <div className="mb-3 sm:mb-6 backdrop-blur-xl bg-red-50/90 border border-red-200/50 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded-xl sm:rounded-2xl text-xs sm:text-sm shadow-sm"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {showDeleteConfirm && (
                <div className="mb-3 sm:mb-6 backdrop-blur-xl bg-red-50/90 border border-red-200/50 rounded-xl sm:rounded-2xl shadow-sm overflow-hidden"
                     style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                  <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-red-200/30 bg-red-100/50">
                    <h3 className="text-sm sm:text-base font-bold text-red-800 flex items-center">
                      <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                      Confirm Delete
                    </h3>
                  </div>
                  <div className="p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-red-700 mb-3 sm:mb-4">
                      Are you sure you want to permanently delete this team member? This action cannot be undone.
                    </p>
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                      <button
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        className="inline-flex items-center justify-center px-4 py-3 text-sm font-bold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                        style={{ minHeight: '44px' }}
                      >
                        {deleteLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                            Deleting...
                          </>
                        ) : (
                          'Confirm Delete'
                        )}
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="inline-flex items-center justify-center px-4 py-3 text-sm font-bold text-gray-700 bg-gray-100/80 backdrop-blur-xl rounded-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
                        style={{ 
                          minHeight: '44px',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)'
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Single Column Layout */}
              <div className="space-y-1.5 sm:space-y-6">
                {/* Team Member Info with Glass Morphism */}
                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30">
                    <h3 className="text-xs sm:text-base font-bold text-gray-900">Team Member</h3>
                  </div>
                  <div className="p-2 sm:p-6">
                    {/* Mobile-optimized member info layout */}
                    <div className="flex items-start space-x-2 mb-2 sm:mb-6">
                      <div className="h-8 w-8 sm:h-16 sm:w-16 rounded-lg sm:rounded-2xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0 text-xs sm:text-base overflow-hidden">
                        {localResource.avatar_url ? (
                          <img 
                            src={localResource.avatar_url} 
                            alt={`${localResource.first_name} ${localResource.last_name}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          `${localResource.first_name[0]}${localResource.last_name[0]}`
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm sm:text-lg font-bold text-gray-900 mb-0 sm:mb-1">
                          {localResource.first_name} {localResource.last_name}
                        </div>
                        <div className="text-xs sm:text-base text-gray-600 truncate">{localResource.email}</div>
                        {localResource.phone && (
                          <div className="text-xs sm:text-base text-gray-600">{localResource.phone}</div>
                        )}
                        <div className="flex items-center justify-between mt-1 sm:mt-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-lg shadow-sm ${
                            localResource.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {localResource.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                          
                          {/* Activate/Deactivate Button */}
                          {localResource.status === 'inactive' ? (
                            <button
                              onClick={handleActivate}
                              disabled={activateLoading}
                              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-white bg-green-600 rounded-xl hover:bg-green-700 disabled:opacity-50 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ml-3"
                              style={{ minHeight: '32px' }}
                            >
                              {activateLoading ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                  Activating...
                                </>
                              ) : (
                                <>
                                  <UserCheck className="h-3 w-3 mr-2" />
                                  Activate
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              onClick={handleDeactivate}
                              disabled={deactivateLoading}
                              className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-50 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ml-3"
                              style={{ minHeight: '32px' }}
                            >
                              {deactivateLoading ? (
                                <>
                                  <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                                  Deactivating...
                                </>
                              ) : (
                                <>
                                  <UserX className="h-3 w-3 mr-2" />
                                  Deactivate
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        
                        {/* Change Password Button */}
                        <div className="mt-3 flex justify-end">
                          <button
                            onClick={() => setShowChangePasswordModal(true)}
                            className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation ml-3"
                            style={{ minHeight: '32px' }}
                          >
                            <Key className="h-3 w-3 mr-2" />
                            Set Login Password
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <BasicInformation
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>

                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <PermissionRoleSection
                    resource={localResource}
                    editMode={editMode}
                    onEdit={setEditMode}
                    onUpdate={onUpdate}
                  />
                </div>

                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <CompensationSection
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>

                <div className="backdrop-blur-xl bg-white/70 rounded-2xl border border-purple-200/30 overflow-hidden shadow-lg"
                     style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                  <AvailabilitySection
                    resource={localResource}
                    editMode={editMode}
                    formData={formData}
                    setFormData={setFormData}
                    onSave={handleSave}
                    onEdit={setEditMode}
                    loading={loading.info}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        resource={localResource}
      />
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
} 